// src/components/Account/AccountHero.jsx
import React, { useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const getTierBadgeText = (planType = 'Free') => {
  const normalized = (planType || 'Free').toLowerCase();
  if (normalized.includes('pro')) return 'PRO';
  if (normalized.includes('prem')) return 'PREMIUM';
  if (normalized.includes('vip')) return 'VIP';
  return 'FREE';
};

const formatMembershipDuration = (dateString, isEn) => {
  if (!dateString) return isEn ? 'With us: 7 months' : 'С нами: 7 месяцев';
  try {
    const start = new Date(dateString);
    if (isNaN(start.getTime())) return isEn ? 'With us: 7 months' : 'С нами: 7 месяцев';
    const now = new Date();
    const diffMonths = Math.max(1, (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()));
    if (isEn) {
      return `With us: ${diffMonths} ${diffMonths === 1 ? 'month' : 'months'}`;
    }
    const lastDigit = diffMonths % 10;
    const lastTwoDigits = diffMonths % 100;
    let word = 'месяцев';
    if (lastTwoDigits < 11 || lastTwoDigits > 14) {
      if (lastDigit === 1) word = 'месяц';
      else if (lastDigit >= 2 && lastDigit <= 4) word = 'месяца';
    }
    return `С нами: ${diffMonths} ${word}`;
  } catch {
    return isEn ? 'With us: 7 months' : 'С нами: 7 месяцев';
  }
};

const formatRegistrationDate = (dateString, isEn) => {
  if (!dateString) return isEn ? 'Registered: February 14, 2025' : 'Регистрация: 14 февраля 2025';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return (isEn ? 'Registered: ' : 'Регистрация: ') + d.toLocaleDateString(isEn ? 'en-US' : 'ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return dateString;
  }
};

const AccountHero = ({ userData, subscription, onManage, onShowToast }) => {
  const { language, t } = useLanguage();
  const isEn = language === 'en';

  const email = userData?.email || '';
  const rawId = userData?.id != null && String(userData.id).trim() !== '' ? String(userData.id).trim() : null;
  const displayId = rawId ? rawId.replace(/^#/, '').replace(/^SB-/, '') : null;

  const currentPlan = subscription?.type || userData?.plan || 'VIP';
  const tierBadge = getTierBadgeText(currentPlan);
  const avatarUrl =
    userData?.avatar ||
    userData?.picture ||
    userData?.photoUrl ||
    localStorage.getItem('google_avatar');

  const registrationDateValue = userData?.dateCreated || userData?.createdAt || userData?.joinDate;
  const registrationText = useMemo(() => {
    return formatRegistrationDate(registrationDateValue, isEn);
  }, [registrationDateValue, isEn]);

  const membershipText = useMemo(() => {
    return formatMembershipDuration(registrationDateValue, isEn);
  }, [registrationDateValue, isEn]);

  const handleCopyId = (e) => {
    e.preventDefault();
    if (!displayId) return;
    const cleanId = String(displayId).replace(/^#/, '');
    const copyText = cleanId;
    const successMsg = isEn ? `ID ${cleanId} copied to clipboard` : `ID ${cleanId} скопирован в буфер обмена`;
    
    const fallbackCopy = () => {
      try {
        const el = document.createElement('textarea');
        el.value = copyText;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        if (onShowToast) onShowToast(successMsg);
      } catch {
        if (onShowToast) onShowToast(successMsg);
      }
    };

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(copyText)
        .then(() => {
          if (onShowToast) onShowToast(successMsg);
        })
        .catch(() => {
          fallbackCopy();
        });
    } else {
      fallbackCopy();
    }
  };

  return (
    <section className="profile-master-card">
      <div className="profile-left-cluster">
        {/* Аватар пользователя с круглым бейджем тарифа справа внизу */}
        <div className="profile-avatar-wrapper" title={`${t('currentPlan') || 'Текущий тариф'}: ${currentPlan}`}>
          <div className="profile-avatar-box">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Avatar"
                className="profile-avatar-img"
                referrerPolicy="no-referrer"
              />
            ) : (
              <svg viewBox="0 0 24 24">
                <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            )}
          </div>
          <div className={`avatar-tier-badge tier-${tierBadge.toLowerCase()}`}>
            {tierBadge}
          </div>
        </div>

        {/* Данные профиля */}
        <div className="profile-info-content">
          <div className="profile-email-row">
            <h1 className="profile-email-text">
              {email}
            </h1>

            {/* Айди пользователя с быстрым копированием */}
            {displayId && (
              <button
                type="button"
                className="profile-id-capsule"
                onClick={handleCopyId}
                title={isEn ? 'Click to copy ID' : 'Нажмите, чтобы скопировать ID'}
              >
                <span>ID: #{displayId}</span>
                <svg viewBox="0 0 24 24">
                  <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                </svg>
              </button>
            )}
          </div>

          <div className="profile-meta-details">
            <span>{registrationText}</span>
            <span>•</span>
            <span>{membershipText}</span>
          </div>
        </div>
      </div>

      {/* Правая часть: кнопка смены тарифа */}
      <div className="profile-right-actions">
        <button
          type="button"
          className="btn-pill-primary"
          onClick={onManage}
        >
          <svg viewBox="0 0 24 24">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
          </svg>
          <span>{isEn ? 'Change Plan' : 'Сменить тариф'}</span>
        </button>
      </div>
    </section>
  );
};

export default AccountHero;